import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  IconButton,
  Grid,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { makeStyles } from '@mui/styles';
import classes from './submit.module.scss';
import CustomButton from '../../UI/CustomButton';
import { TJobOffer } from '../../../types/JobOffer';
import { confirmJobApplication, rejectJobApplication, seenJobApplication } from '../../../apis/job';
import { getUsersResumes } from '../../../apis/user';
import { TEducation, TWorkExperience } from '../../../types/User';
import EducationalCard from '../../Card/EducationalCard';
import WorkExperienceCard from '../../Card/WorkExperienceCard';
import {
  getGenderTranslateValue,
  getJobGroupTranslateValue,
  getMaritalTranslateValue,
  getMilitaryStatusTranslateValue,
} from '../../../constants/cv';

const useStyles = makeStyles(() => ({
  paper: {
    width: '100%',
    borderRadius: '1rem',
  },
}));

interface SubmitDialogProps {
  openSubmitDialog: boolean;
  closeDialog: () => void;
  selectJobApplication: TJobOffer;
  getJobApplicationData: () => void;
}

const SubmitDialog: React.FC<SubmitDialogProps> = (props) => {
  const { openSubmitDialog, closeDialog, selectJobApplication, getJobApplicationData } = props;
  const styles = useStyles();
  const { applicationId, status, resumeId } = selectJobApplication || {};
  const [userInfo, setUserInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  const {
    name,
    lastName,
    age,
    email,
    mobileNumber,
    gender,
    maritalStatus,
    fieldOfExpertise,
    militaryStatus,
    residenceProvince,
    address,
    careerBio,
    educationalBackground,
    workExperience,
  } = userInfo || {};

  const onConfirmJobApplication = () => {
    setConfirmLoading(true);
    confirmJobApplication(applicationId)
      .then(() => getJobApplicationData())
      .catch((e) => console.log({ e }))
      .finally(() => {
        closeDialog();
        setConfirmLoading(false);
      });
  };

  const onRejectJobApplication = () => {
    setRejectLoading(true);
    rejectJobApplication(applicationId)
      .then(() => getJobApplicationData())
      .catch((e) => console.log({ e }))
      .finally(() => {
        closeDialog();
        setRejectLoading(false);
      });
  };

  const onSeenJobApplication = () => {
    seenJobApplication(applicationId)
      .then(() => getJobApplicationData())
      .catch((e) => console.log({ e }));
  };

  const onGetUserInfo = () => {
    setLoading(true);
    getUsersResumes(`?resume_id=${resumeId}`)
      .then((res: any) => setUserInfo(res))
      .catch((e) => console.log({ e }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (openSubmitDialog && status === 0) onSeenJobApplication();
  }, [status, openSubmitDialog]);

  useEffect(() => {
    if (openSubmitDialog && resumeId) onGetUserInfo();
  }, [resumeId, openSubmitDialog]);

  return (
    <Dialog
      onClose={closeDialog}
      open={openSubmitDialog}
      maxWidth='xl'
      classes={{ paper: styles.paper }}
    >
      <Box className={classes.mainDialog}>
        <Box className={classes.header}>
          <Typography color='text.main' fontWeight='bold'>
            بررسی رزومه دریافتی
          </Typography>
          <IconButton onClick={closeDialog}>
            <CloseRoundedIcon color='action' />
          </IconButton>
        </Box>

        {loading ? (
          <Box className='flexCenter' sx={{ height: 300 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <>
            <Box className={classes.gridContainer}>
              <Box className={classes.gridItemContainer}>
                <Box className={classes.rowContainer}>
                  <Typography color='text.light' fontSize='small'>
                    نام کاربر:
                  </Typography>
                  <Typography color='text.main' fontWeight='bold'>
                    {name} {lastName}
                  </Typography>
                </Box>

                <Box className={classes.rowContainer}>
                  <Typography color='text.light' fontSize='small'>
                    شماره همراه:
                  </Typography>
                  <a
                    href={`tel:${mobileNumber}`}
                    style={{ textDecoration: 'none', direction: 'ltr' }}
                  >
                    {mobileNumber}
                  </a>
                </Box>

                <Box className={classes.rowContainer}>
                  <Typography color='text.light' fontSize='small'>
                    ایمیل:
                  </Typography>
                  <Typography color='text.main' fontWeight='bold'>
                    {email}
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.gridItemContainer}>
                <Box className={classes.rowContainer}>
                  <Typography color='text.light' fontSize='small'>
                    سن کاربر:
                  </Typography>
                  <Typography color='text.main' fontWeight='bold'>
                    {age}
                  </Typography>
                </Box>

                <Box className={classes.rowContainer}>
                  <Typography color='text.light' fontSize='small'>
                    جنسیت:
                  </Typography>
                  <Typography color='text.main' fontWeight='bold'>
                    {getGenderTranslateValue(gender)}
                  </Typography>
                </Box>

                <Box className={classes.rowContainer}>
                  <Typography color='text.light' fontSize='small'>
                    وضعیت تاهل:
                  </Typography>
                  <Typography color='text.main' fontWeight='bold'>
                    {getMaritalTranslateValue(maritalStatus)}
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.gridItemContainer}>
                <Box className={classes.rowContainer}>
                  <Typography color='text.light' fontSize='small'>
                    گروه شغلی:
                  </Typography>
                  <Typography color='text.main' fontWeight='bold'>
                    {getJobGroupTranslateValue(fieldOfExpertise)}
                  </Typography>
                </Box>

                <Box className={classes.rowContainer}>
                  <Typography color='text.light' fontSize='small'>
                    وضعیت نظام وظیفه:
                  </Typography>
                  <Typography color='text.main' fontWeight='bold'>
                    {getMilitaryStatusTranslateValue(militaryStatus)}
                  </Typography>
                </Box>

                <Box className={classes.rowContainer}>
                  <Typography color='text.light' fontSize='small'>
                    استان:
                  </Typography>
                  <Typography color='text.main' fontWeight='bold'>
                    {residenceProvince}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box className={classes.gridItemContainer} mt={2}>
              <Box className={classes.rowContainer}>
                <Typography color='text.light' fontSize='small'>
                  آدرس:
                </Typography>
                <Typography color='text.main' fontWeight='bold'>
                  {address}
                </Typography>
              </Box>
              <Box className={classes.rowContainer}>
                <Typography color='text.light' fontSize='small'>
                  بیوگرافی کاربر:
                </Typography>
                <Typography color='text.main' fontWeight='bold'>
                  {careerBio}
                </Typography>
              </Box>
            </Box>

            <Box mt={2}>
              {educationalBackground?.length !== 0 && (
                <Typography color='text.main'>سابقه ی تحصیلات</Typography>
              )}

              <Grid container spacing={2}>
                {educationalBackground?.map((item: TEducation) => (
                  <Grid item lg={3} key={item.id}>
                    <EducationalCard education={item} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box mt={2}>
              <Divider />
            </Box>

            <Box mt={2}>
              {workExperience?.length !== 0 && (
                <Typography color='text.main'>سابقه ی شغلی</Typography>
              )}

              <Grid container spacing={2}>
                {workExperience?.map((item: TWorkExperience) => (
                  <Grid item lg={3} key={item.id}>
                    <WorkExperienceCard workExperience={item} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box className={classes.footer}>
              <CustomButton
                fullWidth
                variant='contained'
                onClick={onRejectJobApplication}
                loading={rejectLoading}
                disabled={rejectLoading}
              >
                رد رزومه
              </CustomButton>

              <CustomButton
                fullWidth
                color='secondary'
                variant='contained'
                onClick={onConfirmJobApplication}
                loading={confirmLoading}
                disabled={confirmLoading}
              >
                تایید رزومه
              </CustomButton>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default SubmitDialog;
